import { createFileRoute, Link } from '@tanstack/react-router'
import { useGetMetrics } from '@/admin/hooks'
import { MetricCard } from '@/admin/components/MetricCard'

export const Route = createFileRoute('/admin/dashboard')({
    component: AdminDashboard,
})

function AdminDashboard() {
    const { data: metrics, isLoading, error } = useGetMetrics()

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="text-center">
                    <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-t-2 border-b-2 border-gray-900"></div>
                    <p className="text-gray-600">Loading metrics...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-lg">
                    <div className="mb-4 text-6xl">⚠️</div>
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">
                        Error Loading Metrics
                    </h1>
                    <p className="mb-6 text-gray-600">
                        {error instanceof Error
                            ? error.message
                            : 'Failed to load dashboard metrics'}
                    </p>
                </div>
            </div>
        )
    }

    if (!metrics) return null

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">
                    Admin Dashboard
                </h1>
                <p className="mt-2 text-gray-600">
                    Platform metrics and insights
                </p>
            </div>

            {/* Active Users Section */}
            <section className="mb-8">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Active Users
                </h2>
                <div className="grid gap-4 md:grid-cols-3">
                    <MetricCard
                        title="Daily Active"
                        value={metrics.activeUsers.daily.total}
                        segmentation={{
                            signedIn: metrics.activeUsers.daily.signedIn,
                            signedInPercentage:
                                metrics.activeUsers.daily.signedInPercentage,
                        }}
                        icon="👤"
                    />
                    <MetricCard
                        title="Weekly Active"
                        value={metrics.activeUsers.weekly.total}
                        segmentation={{
                            signedIn: metrics.activeUsers.weekly.signedIn,
                            signedInPercentage:
                                metrics.activeUsers.weekly.signedInPercentage,
                        }}
                        icon="📅"
                    />
                    <MetricCard
                        title="Monthly Active"
                        value={metrics.activeUsers.monthly.total}
                        segmentation={{
                            signedIn: metrics.activeUsers.monthly.signedIn,
                            signedInPercentage:
                                metrics.activeUsers.monthly.signedInPercentage,
                        }}
                        icon="📊"
                    />
                </div>
            </section>

            {/* Growth Metrics */}
            <section className="mb-8">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Growth Metrics
                </h2>
                <div className="grid gap-4 md:grid-cols-3">
                    <MetricCard
                        title="New Signups"
                        value={metrics.signups.total}
                        breakdown={{
                            Today: metrics.signups.today,
                            'This Week': metrics.signups.thisWeek,
                            'This Month': metrics.signups.thisMonth,
                        }}
                        icon="✨"
                    />
                    <MetricCard
                        title="Product Imports"
                        value={metrics.productImports.total}
                        breakdown={{
                            Today: metrics.productImports.today,
                            'This Week': metrics.productImports.thisWeek,
                            'This Month': metrics.productImports.thisMonth,
                        }}
                        icon="📦"
                    />
                    <MetricCard
                        title="Try-Ons Generated"
                        value={metrics.tryOnsGenerated.total}
                        breakdown={{
                            Today: metrics.tryOnsGenerated.today,
                            'This Week': metrics.tryOnsGenerated.thisWeek,
                            'This Month': metrics.tryOnsGenerated.thisMonth,
                        }}
                        icon="👔"
                    />
                    <MetricCard
                        title="Products Saved"
                        value={metrics.productsSaved.total}
                        breakdown={{
                            Today: metrics.productsSaved.today,
                            'This Week': metrics.productsSaved.thisWeek,
                            'This Month': metrics.productsSaved.thisMonth,
                        }}
                        icon="💾"
                    />
                    <MetricCard
                        title="Collections Created"
                        value={metrics.collectionsCreated.total}
                        breakdown={{
                            Today: metrics.collectionsCreated.today,
                            'This Week': metrics.collectionsCreated.thisWeek,
                            'This Month': metrics.collectionsCreated.thisMonth,
                        }}
                        icon="📁"
                    />
                </div>
            </section>

            {/* Quick Links */}
            <section className="mt-8">
                <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Admin Actions
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                    <Link
                        to="/admin/reports"
                        className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 p-4 transition hover:bg-red-100"
                    >
                        <div>
                            <h3 className="font-semibold text-red-900">Reports Queue</h3>
                            <p className="text-sm text-red-700">
                                Review and moderate user reports (24hr response required)
                            </p>
                        </div>
                        <span className="text-2xl">🚨</span>
                    </Link>
                    <Link
                        to="/admin/pending-users"
                        className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-4 transition hover:bg-blue-100"
                    >
                        <div>
                            <h3 className="font-semibold text-blue-900">Pending Users</h3>
                            <p className="text-sm text-blue-700">
                                Review and approve new stylist signups
                            </p>
                        </div>
                        <span className="text-2xl">👥</span>
                    </Link>
                </div>
            </section>
        </div>
    )
}
